import { ROOT_URL } from '@utils/apiHooks';
import { downloadICS, useMobileWidth } from '@utils/hooks';

import Button from '@components/Button';
import { bookingLink } from '@constants/mappings';
import dayjs from 'dayjs';
import { formatDateToICS } from '@utils/time';
import { getEventDetails } from '@store/slices/eventSlice';
import { useInbound } from '@utils/Inbound/useInbound';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

interface SaveTheDateProps {
  label: string;
  buttonType: string;
  eventId?: string;
  padding?: string;
  width?: string;
}

const SaveTheDate = ({
  label,
  buttonType,
  eventId,
  padding,
  width,
}: SaveTheDateProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const isMobile = useMobileWidth();

  const { actionInbound, inboundCookie } = useInbound();

  const eventDescription = useMemo(() => {
    const venueCity = [
      sluggedEvent?.location?.name,
      sluggedEvent?.location?.marker?.city,
    ]
      .filter(Boolean)
      .join(', ');
    const eventDescriptionItems = [
      { title: 'event-name', value: sluggedEvent?.name },
      { title: 'venue-&-city', value: venueCity },
      { title: 'event-website', value: sluggedEvent?.name },
      { title: 'agenda', value: '' },
      { title: 'attendee-list', value: '' },
      {
        title: 'booking-link',
        value: `${bookingLink[ROOT_URL]}purchase/${sluggedEvent?.slug}`,
      },
    ];
    return eventDescriptionItems
      .map(item => `${t(item.title)}: ${item.value}`)
      .join('\n');
  }, [
    sluggedEvent?.location?.marker?.city,
    sluggedEvent?.location?.name,
    sluggedEvent?.name,
    sluggedEvent?.slug,
    t,
  ]);

  const icsContent = useMemo(
    () =>
      `BEGIN:VCALENDAR
VERSION:2.0
CHARSET=ISO-8859-1
PRODID:-//we.CONECT Global Leaders//Website//EN
CALSCALE:GREGORIAN
BEGIN:VEVENT
UID:${btoa(sluggedEvent?.name)}
DTSTAMP:${dayjs().unix()}
LOCATION:${sluggedEvent?.location?.name ?? ''}
DESCRIPTION:${eventDescription}
SUMMARY:${sluggedEvent?.name}
DTSTART:${formatDateToICS(sluggedEvent?.start_date)}
DTEND:${formatDateToICS(sluggedEvent?.end_date)}
END:VEVENT
END:VCALENDAR`,
    [
      eventDescription,
      sluggedEvent?.end_date,
      sluggedEvent?.location?.name,
      sluggedEvent?.name,
      sluggedEvent?.start_date,
    ]
  );

  return (
    <Button
      title={label}
      backgroundColor={sluggedEvent?.design?.themeColor}
      hoverBackgroundColor={theme.color.mobilityGrey}
      textColor={theme.color.white}
      lineHeight={theme.lineHeight.height150}
      fontSize={isMobile ? theme.fontSize.size18 : theme.fontSize.size20}
      borderRadius={theme.borderRadius.radius10}
      padding={padding ?? (isMobile ? '1.5rem 2.5rem' : '1.1rem 4.9rem')}
      width={width ?? (isMobile ? '100%' : 'max-content')}
      onClick={() => {
        void (async () => {
          downloadICS(icsContent, 'calendar', t);

          if (inboundCookie) {
            await actionInbound({
              ...JSON.parse(inboundCookie ?? '{}'),
              event: sluggedEvent?.id,
              path: window.location.href,
              action: 'calendar',
            });
          }
        })();
      }}
    />
  );
};

export default SaveTheDate;
